import { createReducer } from 'redux-starter-kit';
import { selectRequest } from '../../../domain/requests/RequestReducer';
import {
  editChecklistItemNew, cancelEditChecklistItem, fetchAddChecklistItem,
  fetchUpdateChecklistItem, addPublishChecklistItem,
  removePublishChecklistItem, fetchChecklistItems, fetchChecklistItemReferences,
} from './checklistItemUiActions';

/**
 * A selector for those Checklist Item reference areas in the domain.
 * @param {state} state The redux state for the application.
 */
export const selectChecklistItemReferencesMap = state => state.via.domain.checklistItemReferences;

/**
 * A selector for finding the current edit state.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the Checklist Item being edited.
 * @example const editState = selectEditState(state);
 */
export const selectEditState = state => state.via.ui.checklistItems.editState;

/**
 * A selector for finding the current publishing checklist item codes.
 * @param {object} state The redux state of the application.
 * @returns An array of Checklist Item codes.
 * @example const loadingChecklistItemCodes = selectLoadingChecklistItemCodes(state);
 */
export const selectLoadingChecklistItemCodes = state => state.via.ui.items.pendingPublishChecklistItems;

/**
 * A selector that returns the state of the 'FETCH_CHECKLIST_CATEGORIES' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const checklistItemRequest = selectChecklistItemsRequest(state);
 */
export const selectChecklistItemsRequest = (state) => {
  const requestId = state.via.ui.checklistItems.requestChecklistItemsId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the current 'FETCH_CREATE_CHECKLIST_CATEGORY' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const addChecklistItemRequest = selectAddChecklistItemRequest(state);
 */
export const selectSubmitChecklistItemRequest = (state) => {
  const requestId = state.via.ui.checklistItems.editState.requestSubmitChecklistItemsId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector that returns the state of the 'FETCH_CHECKLIST_CATEGORY_REFERENCES' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const checklistItemReferencesRequest = selectChecklistItemReferencesRequest(state);
 */
export const selectChecklistItemReferencesRequest = (state) => {
  const requestId = state.via.ui.items.requestChecklistItemReferencesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for those Checklist Item reference areas loaded by the Checklist Item Configuration screen.
 * @param {state} state The redux state for the application.
 * @returns The Checklist Item references areas that were loaded.
 * @example const checklistItemReferences = selectChecklistItemReferences(state);
 */
export const selectChecklistItemReferences = (state) => {
  const checklistItemReferencesMap = selectChecklistItemReferencesMap(state);
  const checklistItemReferences = Object.values(checklistItemReferencesMap);

  return checklistItemReferences || [];
};

const intitialEditingState = {
  isEditing: false,
  itemCode: null,
  requestSubmitChecklistItemId: null,
};

const checklistItemsInitialState = {
  editState: intitialEditingState, // Is a Checklist Item being edited/created.
  pendingPublishChecklistItems: [], // Checklist Items being submitted for publishing.
  requestChecklistItemsId: null, // The request id for loading Checklist Items.
  requestChecklistItemReferencesId: null, // The request id for loading those items that are referenced by a Checklist Item.
};

/**
 * A reducer for handling the UI state changes for the Checklist Item Configuration screen.
 */
export const checklistItemsReducer = createReducer(checklistItemsInitialState, {
  // The user has started editing the Checklist Item.
  [editChecklistItemNew]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      isEditing: true,
      itemCode: action.payload,
    },
  }),

  // The user has stopped editing the Checklist Item.
  [cancelEditChecklistItem]: state => ({
    ...state,
    editState: {
      ...intitialEditingState,
    },
  }),

  // A new requst has been made to add a Checklist Item.
  [fetchAddChecklistItem]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitChecklistItemId: action.payload,
    },
  }),

  // A new request has been made to update a Checklist Item.
  [fetchUpdateChecklistItem]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitChecklistItemId: action.payload,
    },
  }),

  // A Checklist Item is being published. So add it to state.
  [addPublishChecklistItem]: (state, action) => ({
    ...state,
    pendingPublishChecklistItems: [...state.pendingPublishChecklistItems, action.payload],
  }),

  // A Checklist Item has finished publishing. Remove it from state.
  [removePublishChecklistItem]: (state, action) => ({
    ...state,
    pendingPublishChecklistItems: state.pendingPublishChecklistItems.filter(code => `${code}` !== `${action.payload}`),
  }),

  // A new request has been made for the Checklist Items.
  [fetchChecklistItems]: (state, action) => ({
    ...state,
    requestChecklistItemsId: action.payload,
  }),

  // A new request has been made for those areas referenced by a Checklist Item.
  [fetchChecklistItemReferences]: (state, action) => ({
    ...state,
    requestChecklistItemReferencesId: action.payload,
  }),

});

export default checklistItemsReducer;
